// L'accueil de Radar (lot 2 de la refonte, 2026-10-11). Il remplace « Mes
// opportunités », une pile à trier avec un curseur et deux groupes de score.
//
// CE QUI ATTEND UN GESTE, ET RIEN D'AUTRE (issue #168, 2026-10-14). La
// première version de cet écran prouvait que Radar travaille : date, dernière
// relecture, « 3 employeurs et 2 sites suivis, 58 nouvelles offres cette
// nuit, dont 10 dans vos métiers », prochaine relecture, puis la liste des
// employeurs et un pied de réglages. Mais on arrive ici par le mail, qui a déjà
// fait cette preuve ; et dès la deuxième visite, ces chiffres ne menaient à
// aucun geste. L'écran répond maintenant, dans l'ordre :
//
//  1. « Qu'ai-je à faire ? » Les candidatures dont l'étape suivante demande un
//     geste (envoyer, relancer, préparer l'entretien), d'après
//     `prochaineEtape`. Absent quand il n'y a rien ;
//  2. « Y a-t-il quelque chose pour moi ? » Les offres au-dessus du seuil
//     d'alerte, le même que celui des mails. Ce qui est juste en dessous
//     reste replié, sauf quand rien n'est au-dessus : ce sont alors « les
//     plus proches », ouvertes (issue #190) ;
//  3. Une seule ligne d'état, en bas : Radar a relu vos sites, quand.
//
// Les employeurs vivent dans leur onglet ; seul un compte qui n'en suit aucun
// voit ici de quoi en choisir. Les réglages (seuil, rythme, date du CV) vivent
// dans Réglages. Pas de compteur ni de graphique ajouté à la place (décision
// du 2026-10-11 : des phrases plus courtes, rien de plus).
//
// Les offres viennent de `/api/radar/accueil` (services/accueilRadar.ts).

import { useSuivreSite } from '../components/filtreSite'
import { useEffect, useState, type CSSProperties } from 'react'
import { ChevronDown, ChevronRight, FileUp } from 'lucide-react'
import { MOUVEMENT, ScoreRing, mouvementReduit, scoreTone } from '@ai-job-fit/ui'
import { get, post, type Summary, type SuiviRow } from '../lib/api'
import { useData, Loading, ActionButton, useToast } from '../components/common'
import { useT } from '../lib/i18n'
import { jourCourt, parseDate, relTime } from '../lib/format'
import { navigate, useQuery } from '../lib/router'
import { lienFiche } from '../lib/file'
import { Employeur, LogoEntreprise } from '../components/sites'
import type { Rythme } from '../components/rythme'
import type { LigneEmployeur, PageEnAttente, Pont } from '../components/employeurs'
import { signalerTache } from '../components/bandeauTaches'
import { BoutonConnecterExtension, BoutonWebStore, surOrdinateur } from '../components/installerExtension'
import { aFaire, type ChoseAFaire } from '../lib/prochaineEtape'
import { useExtensionInstallee } from '../lib/extensionInstallee'
import { useLang } from '../lib/lang'
import { useMots } from '../lib/vocabulaire'
import { faitsEnMots, type FaitsOffre } from '../../faitsOffre'
import { premiereFoisCetteSession } from '../lib/mouvement'

export interface CarteOffre {
  id: number
  titre: string
  employeur: string | null
  site: string
  lieu: string | null
  score: number
  atouts: string[]
  manques: string[]
  url: string
  logo: string | null
  nouvelle: boolean
  vueLe: string
  faits?: FaitsOffre | null
}

interface AccueilData {
  seuil: number
  rythme: Rythme | null
  coupes: boolean
  /** Fin de la pause des mails (issue #175), ou null. */
  pause: string | null
  derniereNuit: string | null
  veille: { employeurs: number; annonces: number; enLigne: number; nouvelles: number; nouvellesDansMetiers: number }
  pourVous: CarteOffre[]
  pourVousTotal: number
  horsRegions: number
  exclues: number
  justeEnDessous: CarteOffre[]
  cvDu: string | null
  employeurs: LigneEmployeur[]
  pages: PageEnAttente[]
  pont: Pont | null
  suggestion: Suggestion | null
}

export function Accueil({ summary }: { summary: Summary | null }) {
  // Les anneaux des offres montent une fois par session (#183) : revenir de
  // la fiche montre la liste telle quelle.
  const [animer, setAnimer] = useState(() => premiereFoisCetteSession('accueil'))
  const t = useT()
  const data = useData(() => get<AccueilData>('/api/radar/accueil'), [])
  const candidatures = useData(() => get<SuiviRow[]>('/api/candidatures'), [])
  // La liste a joué son arrivée : les cartes montées ensuite (après « Plus
  // tard », un rechargement) s'affichent telles quelles.
  const chargee = !!data.data
  useEffect(() => {
    if (!animer || !chargee) return
    const fin = setTimeout(() => setAnimer(false), 2500)
    return () => clearTimeout(fin)
  }, [animer, chargee])
  const toast = useToast()
  const mots = useMots()
  // Revenu d'une file triée jusqu'au bout (issue #170).
  const [params] = useQuery()
  const finDeFile = params.get('fin') === '1'

  // Trancher sans ouvrir la fiche (issue #169) : une offre évidemment hors
  // sujet s'écarte d'un clic. « Annuler » la remet à décider, comme « Écartées »
  // dans Mes offres ; la raison d'un refus reste à donner sur la fiche.
  // « Plus tard » et « Pas pour moi » replient la carte au lieu de la faire
  // sauter (#185) ; « Annuler » la fait revenir par le même chemin.
  const [partantes, setPartantes] = useState<Set<number>>(() => new Set())
  const [revenue, setRevenue] = useState<number | null>(null)
  // « revient » ne vaut qu'au retour : une carte remontée plus tard ne rejoue rien.
  useEffect(() => {
    if (revenue === null) return
    const fin = setTimeout(() => setRevenue(null), 1500)
    return () => clearTimeout(fin)
  }, [revenue])
  const mouvementDe = (id: number, arrive: boolean): 'arrive' | 'part' | 'revient' | undefined =>
    partantes.has(id) ? 'part' : revenue === id ? 'revient' : arrive ? 'arrive' : undefined
  async function trancher(o: CarteOffre, statut: 'later' | 'skip') {
    const glisse = !mouvementReduit()
    if (glisse) setPartantes((p) => new Set(p).add(o.id))
    const retirer = () => setPartantes((p) => { const n = new Set(p); n.delete(o.id); return n })
    try {
      await Promise.all([
        post(`/api/offers/${o.id}/status`, { status: statut }),
        glisse ? new Promise((fin) => setTimeout(fin, MOUVEMENT.arrivee)) : null,
      ])
    } catch (e: any) {
      // Échec : la carte revient par le même chemin, sans rejouer l'arrivée du matin.
      retirer()
      setRevenue(o.id)
      toast(e.message, 'err')
      return
    }
    // La carte reste repliée jusqu'à ce que la liste rechargée l'enlève :
    // la retirer de `partantes` maintenant la ferait réapparaître un instant.
    data.reload()
    toast(t('accueil2.tranchee', { etat: mots(statut === 'later' ? 'etat.later' : 'etat.skip'), titre: o.titre }), 'ok', {
      label: t('common.cancel'),
      onClick: async () => {
        try {
          await post(`/api/offers/${o.id}/status`, { status: 'todo' })
        } catch (e: any) {
          toast(e.message, 'err')
          return
        }
        retirer()
        setRevenue(o.id)
        data.reload()
      },
    })
  }
  const [dessousOuvert, setDessousOuvert] = useState(false)

  if (data.loading) return <Loading label={t('common.loading')} />
  // Pas encore de CV : le serveur répond 409, l'écran envoie le déposer.
  if (data.error || !data.data) {
    return (
      <div className="body acc">
        <section className="acc-vide">
          <FileUp size={26} />
          <h1>{t('bienvenue.cv.titre')}</h1>
          <p>{t('accueil2.sansCv')}</p>
          <button className="btn-primary" onClick={() => navigate('#/bienvenue')}>{t('accueil2.sansCvAction')}</button>
        </section>
      </div>
    )
  }

  const a = data.data
  const plur = (n: number, un: Parameters<typeof t>[0], plus: Parameters<typeof t>[0]) => t(n === 1 ? un : plus, { n })
  const nouvelles = a.pourVous.filter((o) => o.nouvelle).length
  const aucunSuivi = a.employeurs.length === 0 && a.pages.length === 0
  const todo = aFaire(candidatures.data ?? [])

  const titre =
    nouvelles > 0
      ? plur(nouvelles, 'accueil2.titreNouvelleUn', 'accueil2.titreNouvellesPlus').replace('{seuil}', String(a.seuil))
      : a.pourVousTotal > 0
        ? plur(a.pourVousTotal, 'accueil2.titreAttenteUn', 'accueil2.titreAttentePlus').replace('{seuil}', String(a.seuil))
        : t('accueil2.titreCalme', { seuil: a.seuil })

  return (
    <div className="body acc">
      <header className="acc-preuve">
        {finDeFile && <p className="acc-fin" data-testid="acc-fin">{t('accueil2.finFile')}</p>}
        <h1>{titre}</h1>
        {/* Une sous-ligne au plus, et seulement quand elle mène à un geste ou
            répond à « et maintenant ? » quand il n'y a rien. */}
        {aucunSuivi ? (
          <p>
            {t('accueil2.aucunSuivi')}{' '}
            <button className="linkbtn" onClick={() => navigate('#/sources')}>{t('accueil2.choisirEmployeurs')}</button>
          </p>
        ) : a.coupes ? (
          <p>
            {t('accueil2.mailsCoupes')}{' '}
            <button className="linkbtn" onClick={() => navigate('#/config')}>{t('accueil2.mailsReactiver')}</button>
          </p>
        ) : a.pause ? (
          // Dite tant qu'elle dure, sinon on oublie qu'on l'a posée (#175).
          <p>
            {t('accueil2.pause', { date: jourCourt(parseDate(a.pause) ?? new Date()) })}{' '}
            <button className="linkbtn" onClick={async () => { await post('/api/radar/rythme', { pause: 0 }); data.reload() }}>
              {t('accueil2.reprendre')}
            </button>
          </p>
        ) : (
          a.pourVousTotal === 0 && <p>{t('accueil2.lundi')}</p>
        )}
      </header>

      {todo.length > 0 && (
        <section className="acc-sec">
          <div className="acc-eyebrow">{t('accueil2.aFaire')}</div>
          <div className="acc-liste" data-testid="acc-a-faire">
            {todo.map((c) => <LigneAFaire key={c.k.id} c={c} />)}
          </div>
        </section>
      )}

      {a.pourVous.length > 0 && (
        <section className="acc-sec">
          <div className="acc-eyebrow">{t('accueil2.pourVous')}</div>
          <div className="acc-cartes">
            {/* Chaque carte ouvre sa fiche DANS la file des cartes : on passe
                de l'une à l'autre sans revenir ici (issue #170). */}
            {a.pourVous.map((o, k) => (
              <Carte key={o.id} o={{ ...o, url: lienFiche(o.id, a.pourVous.map((x) => x.id)) }} onTrancher={trancher} anime={animer} rang={k}
                mouvement={mouvementDe(o.id, animer)} />
            ))}
          </div>
          {a.pourVousTotal > a.pourVous.length && (
            <p className="field-help">{t('accueil2.pourVousPlus', { n: a.pourVous.length, total: a.pourVousTotal })}</p>
          )}
        </section>
      )}

      {a.pont && <CartePont pont={a.pont} onDone={() => data.reload()} />}
      <CarteExtension />
      {a.suggestion && <CarteSuggestion s={a.suggestion} onDone={() => data.reload()} />}

      {/* Rien ne disparaît en silence : ce que les régions écartent se compte. */}
      {a.horsRegions > 0 && (
        <p className="field-help">
          {plur(a.horsRegions, 'accueil2.horsRegionsUn', 'accueil2.horsRegionsPlus').replace('{seuil}', String(a.seuil))}{' '}
          <button className="linkbtn" onClick={() => navigate('#/config')}>{t('accueil2.changerRegions')}</button>
        </p>
      )}
      {a.exclues > 0 && (
        <p className="field-help">
          {plur(a.exclues, 'accueil2.exclueUn', 'accueil2.excluesPlus')}{' '}
          <button className="linkbtn" onClick={() => navigate('#/config')}>{t('accueil2.changerCriteres')}</button>
        </p>
      )}

      {/* Rien au-dessus du seuil : « juste en dessous » ne désigne plus rien, et
          ce sont les meilleures offres de la semaine. Elles s'affichent donc
          ouvertes, sous leur propre titre (issue #190). */}
      {a.pourVous.length === 0 && a.justeEnDessous.length > 0 && (
        <section className="acc-sec">
          <div className="acc-eyebrow">{t('accueil2.plusProches')}</div>
          <div className="acc-cartes">
            {a.justeEnDessous.map((o) => <Carte key={o.id} o={o} onTrancher={trancher} mouvement={mouvementDe(o.id, false)} />)}
          </div>
          <p className="field-help">
            {t('accueil2.dessousNote')}{' '}
            <button className="linkbtn" onClick={() => navigate('#/config')}>{t('accueil2.changerRythme')}</button>
          </p>
        </section>
      )}

      {a.pourVous.length > 0 && a.justeEnDessous.length > 0 && (
        <section className="acc-sec">
          <button className="acc-repli" aria-expanded={dessousOuvert} onClick={() => setDessousOuvert((v) => !v)}>
            <span>
              <b>{t('accueil2.dessousTitre')}</b>
              <span className="acc-gris">
                {' · '}
                {plur(a.justeEnDessous.length, 'accueil2.dessousUn', 'accueil2.dessousPlus')
                  .replace('{min}', String(Math.max(0, a.seuil - 10)))
                  .replace('{max}', String(a.seuil - 1))}
              </span>
            </span>
            {dessousOuvert ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          {dessousOuvert && (
            <>
              <div className="acc-cartes">
                {a.justeEnDessous.map((o) => <Carte key={o.id} o={o} onTrancher={trancher} mouvement={mouvementDe(o.id, false)} />)}
              </div>
              <p className="field-help">
                {t('accueil2.dessousNote')}{' '}
                <button className="linkbtn" onClick={() => navigate('#/config')}>{t('accueil2.changerRythme')}</button>
              </p>
            </>
          )}
        </section>
      )}

      {/* Le reste des candidatures (envoyées, relance pas encore due) : un
          lien, pas une liste. Le détail vit dans « Mes offres ». */}
      {(summary?.suiviActifs ?? 0) > todo.length && (
        <section className="acc-sec">
          <button className="acc-liste acc-lien" onClick={() => navigate('#/suivi')}>
            <span className="acc-ligne">
              <span className="grow">{plur(summary!.suiviActifs, 'accueil2.enCoursUn', 'accueil2.enCoursPlus')}</span>
              <ChevronRight size={16} />
            </span>
          </button>
        </section>
      )}

      {!aucunSuivi && (
        <p className="acc-pied" data-testid="acc-etat">
          {a.derniereNuit ? t('accueil2.etat', { quand: relTime(a.derniereNuit) }) : t('accueil2.etatPremiere')}
        </p>
      )}
    </div>
  )
}

/** Une candidature qui attend un geste : le geste d'abord, puis le poste.
 *  Mène à la fiche, où le parcours (envoi, relance, entretien) se fait. */
function LigneAFaire({ c }: { c: ChoseAFaire }) {
  const t = useT()
  const { k, etape } = c
  const geste =
    etape.cle === 'relancer'
      ? t('suivi.nextFollowUpDue', { date: jourCourt(etape.date) })
      : t(etape.cle === 'envoyer' ? 'suivi.nextSend' : 'suivi.nextInterview')
  return (
    <button className="acc-ligne acc-lien" onClick={() => navigate(`#/offre/${k.offerId}`)}>
      <LogoEntreprise nom={k.company} logo={k.logo} className="pc-logo acc-logo-sm" />
      <span className="grow">
        <b>{geste}</b>
        <span className="acc-afaire">{k.title} · <Employeur nom={k.company} source={k.source_name} /></span>
      </span>
      <ChevronRight size={16} className="acc-chev" />
    </button>
  )
}

/**
 * Une offre : chez qui, quoi, ce qu'elle dit d'elle-même, pourquoi pour vous,
 * et son score. Le titre est un vrai lien vers la fiche (clic molette compris),
 * étiré sur toute la carte ; les gestes passent au-dessus.
 *
 * Depuis l'issue #169 : les faits de l'offre (contrat, télétravail, salaire)
 * en puces, une raison pour et UNE à vérifier, chacune en entier sur sa ligne.
 * La raison était coupée à deux lignes, et c'était justement la partie qui
 * fait douter (« À vérifier : No demonstrated… ») qui tombait.
 */
export function Carte({ o, sous, onTrancher, anime = false, rang = 0, mouvement }: {
  o: CarteOffre
  sous?: string
  onTrancher?: (o: CarteOffre, statut: 'later' | 'skip') => void
  anime?: boolean
  /** Place dans la cascade d'arrivée (#185). */
  rang?: number
  /** Ce que la carte fait : elle arrive (une fois par session), part (« Plus tard »), revient (« Annuler »). */
  mouvement?: 'arrive' | 'part' | 'revient'
}) {
  const t = useT()
  const mots = useMots()
  const lang = useLang()
  const meta = [o.employeur ?? t('accueil2.via', { site: o.site }), o.lieu].filter(Boolean).join(' · ')
  // Les verdicts finissent souvent par un point, et la phrase en ajoute un :
  // « non explicités.. ». On retire la ponctuation finale de chaque élément.
  const net = (x: string) => x.trim().replace(/[.;,\s]+$/, '')
  const faits = faitsEnMots(o.faits, lang)
  return (
    <div className={`acc-carte${mouvement ? ` ${mouvement}` : ''}`} style={{ '--k': rang } as CSSProperties}>
      <LogoEntreprise nom={o.employeur} logo={o.logo} className="pc-logo acc-logo" />
      <span className="acc-corps">
        <span className="acc-meta">
          {meta}
          {o.nouvelle && <span className="acc-neuf">{t('accueil2.nouvelle')}</span>}
        </span>
        <a className="acc-titre acc-carte-lien" href={o.url}>{o.titre}</a>
        {faits.length > 0 && (
          <span className="acc-faits">{faits.map((f) => <span key={f} className="acc-fait">{f}</span>)}</span>
        )}
        {o.atouts.length > 0 && (
          <span className="acc-raison"><b className="pour">{t('accueil2.atoutsLabel')}</b> {o.atouts.map(net).join(', ')}.</span>
        )}
        {o.manques.length > 0 && (
          <span className="acc-raison"><b className="averif">{t('accueil2.manquesLabel')}</b> {net(o.manques[0])}.</span>
        )}
        {sous && <span className="acc-sous">{sous}</span>}
        {onTrancher && (
          <span className="acc-gestes">
            <button type="button" className="btn-secondary btn-sm" onClick={() => onTrancher(o, 'later')}>{mots('etat.later')}</button>
            <button type="button" className="btn-secondary btn-sm" onClick={() => onTrancher(o, 'skip')}>{mots('etat.skip')}</button>
          </span>
        )}
      </span>
      {o.score >= 0 && <ScoreRing score={o.score} tone={scoreTone(o.score)} size={44} anime={anime} />}
    </div>
  )
}

/**
 * Le pont vers l'extension (lot 3) : deux offres du même employeur gardées
 * depuis LinkedIn. Rien n'est ajouté sans clic ; « Non merci » est retenu. Une
 * page déjà connue se suit tout de suite, une page inconnue s'ajoute depuis
 * « Employeurs », où la demande part.
 */
type Suggestion =
  | { type: 'regions' | 'salaire' | 'metier'; n: number }
  | { type: 'employeur'; n: number; sourceId: number; nom: string }
  // Une semaine de silence (issue #170) : le rythme qui l'aurait rompue.
  | { type: 'seuil'; n: number; rythme: Rythme; seuil: number }

/** Trois « Pas pour moi » pour la même raison : le réglage qui les évite. */
function CarteSuggestion({ s, onDone }: { s: Suggestion; onDone: () => void }) {
  const t = useT()
  const toast = useToast()
  const texte =
    s.type === 'employeur'
      ? t('sugg.employeur', { n: s.n, nom: s.nom })
      : s.type === 'seuil'
        ? t('sugg.seuil', { n: s.n })
        : t(`sugg.${s.type}` as const, { n: s.n })
  return (
    <section className="acc-sec">
      <div className="acc-pont">
        <span>{texte}</span>
        <span className="acc-pont-act">
          <ActionButton
            size="sm"
            variant="primary"
            onClick={async () => {
              if (s.type === 'employeur') {
                await post('/api/radar/rythme', { mails: { employeur: s.sourceId, statut: 'muet' } })
                toast(t('mails.muetAide'))
                onDone()
              } else if (s.type === 'seuil') {
                await post('/api/radar/rythme', { rythme: s.rythme })
                toast(t('sugg.seuilFait', { seuil: s.seuil }))
                onDone()
              } else navigate(s.type === 'metier' ? '#/profil' : '#/config')
            }}
          >
            {s.type === 'seuil'
              ? t('sugg.seuilAction', { seuil: s.seuil })
              : t(`sugg.${s.type}Action` as const, { nom: s.type === 'employeur' ? s.nom : '' })}
          </ActionButton>
          <ActionButton
            size="sm"
            onClick={async () => {
              await post('/api/radar/suggestion', s)
              onDone()
            }}
          >
            {t('pont.non')}
          </ActionButton>
        </span>
      </div>
    </section>
  )
}

function CartePont({ pont, onDone }: { pont: Pont; onDone: () => void }) {
  const t = useT()
  const toast = useToast()
  const { suivre, fenetre } = useSuivreSite()
  return (
    <section className="acc-sec">
      {fenetre}
      <div className="acc-pont">
        <span>{t('pont.texte', { n: pont.gardees, nom: pont.nom })}</span>
        <span className="acc-pont-act">
          {pont.sourceId !== null ? (
            <ActionButton
              size="sm"
              variant="primary"
              onClick={async () => {
                // Le refus (plafond, accès) remonte en notification par
                // ActionButton ; un gros site ouvre la fenêtre de filtre.
                if (!(await suivre(pont.sourceId!, pont.nom))) return
                signalerTache()
                toast(t('sources.followed', { name: pont.nom }))
                onDone()
              }}
            >
              {t('pont.suivre', { nom: pont.nom })}
            </ActionButton>
          ) : (
            <ActionButton size="sm" variant="primary" onClick={() => navigate('#/sources')}>{t('pont.ajouter')}</ActionButton>
          )}
          <ActionButton
            size="sm"
            onClick={async () => {
              await post('/api/radar/pont', { nom: pont.nom })
              toast(t('pont.nonToast'))
              onDone()
            }}
          >
            {t('pont.non')}
          </ActionButton>
        </span>
      </div>
    </section>
  )
}

// « Plus tard » vaut pour CE navigateur, et c'est voulu : l'extension s'installe
// navigateur par navigateur. Trente jours, puis la carte revient une fois.
const CLE_PLUS_TARD = 'radar.extension.plusTard'
const PLUS_TARD_MS = 30 * 86_400_000

function reporteeRecemment(): boolean {
  try {
    const quand = Number(localStorage.getItem(CLE_PLUS_TARD))
    return quand > 0 && Date.now() - quand < PLUS_TARD_MS
  } catch {
    return false
  }
}

/**
 * L'extension, proposée à qui ne l'a pas encore connectée. Elle est publiée sur
 * le Chrome Web Store : on ne la cache plus derrière « Aide et extension ».
 * Jamais au téléphone (une extension Chrome ne s'y installe pas), jamais une
 * fois connectée, et « Plus tard » la fait taire.
 */
function CarteExtension() {
  const t = useT()
  const jeton = useData(() => get<{ actif: boolean }>('/api/extension/token'), [])
  const [masquee, setMasquee] = useState(reporteeRecemment)
  const ordinateur = surOrdinateur()
  // Déjà installée (issue #234) : on propose de la connecter, pas de l'installer.
  const installee = useExtensionInstallee()
  if (masquee || !ordinateur || jeton.loading || jeton.error || jeton.data?.actif || installee === undefined) return null
  return (
    <section className="acc-sec">
      <div className="acc-pont">
        <span>{installee ? t('ext.installee') : t('accueil2.extension.texte')}</span>
        <span className="acc-pont-act">
          {installee ? <BoutonConnecterExtension petit onConnectee={jeton.reload} /> : <BoutonWebStore petit />}
          <button
            className="btn-secondary btn-sm"
            onClick={() => {
              try {
                localStorage.setItem(CLE_PLUS_TARD, String(Date.now()))
              } catch {
                // Stockage refusé (navigation privée) : la carte se masque pour cette visite.
              }
              setMasquee(true)
            }}
          >
            {t('accueil2.extension.plusTard')}
          </button>
        </span>
      </div>
    </section>
  )
}
